import { useEffect, useRef, useState } from "react";
import { colors } from "@patient-watch/icons";
import { useTemplatesCopy } from "@/features/i18n/templatesCopy";
import { ui } from "@patient-watch/ui";
import { Star } from "lucide-react";

import useDeleteFavouriteUser from "../api/deleteFavouriteUser";
import useInsertFavouriteUser from "../api/insertFavouriteUser";

interface Props {
    favourite: boolean;
    userId: string;
}
const FavouriteUserStar = ({ favourite, userId }: Props) => {
    const copy = useTemplatesCopy();
    const favouriteUser = useInsertFavouriteUser();
    const deleteUser = useDeleteFavouriteUser();
    const [optimisticFavourite, setOptimisticFavourite] = useState<boolean | null>(null);
    const displayedFavourite = optimisticFavourite ?? favourite;
    const displayedRef = useRef(displayedFavourite);
    const inFlightRef = useRef(false);
    displayedRef.current = displayedFavourite;
    const isPending = favouriteUser.isPending || deleteUser.isPending;

    useEffect(() => {
        if (optimisticFavourite !== null && favourite === optimisticFavourite) {
            setOptimisticFavourite(null);
        }
    }, [favourite, optimisticFavourite]);

    return (
        <ui.Button
            type="button"
            tabIndex={-1}
            disabled={isPending}
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
            }}
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (isPending || inFlightRef.current) return;
                const nextFavourite = !displayedRef.current;
                displayedRef.current = nextFavourite;
                inFlightRef.current = true;
                setOptimisticFavourite(nextFavourite);
                const unlock = () => {
                    inFlightRef.current = false;
                };
                if (nextFavourite) {
                    favouriteUser.mutate(userId, {
                        onError: () => setOptimisticFavourite(null),
                        onSettled: unlock,
                    });
                    return;
                }
                deleteUser.mutate(userId, {
                    onError: () => setOptimisticFavourite(null),
                    onSettled: unlock,
                });
            }}
            variant="ghost"
            className="p-2 w-fit h-fit [&_svg]:hover:animate-spin-slow"
            aria-label={displayedFavourite ? copy.removeFromFavourites : copy.addToFavourites}
            aria-pressed={displayedFavourite}
        >
            <Star
                fill={displayedFavourite ? colors["indigo-600"] : "transparent"}
                stroke={displayedFavourite ? colors["indigo-600"] : colors["slate-300"]}
            />
        </ui.Button>
    );
};

export default FavouriteUserStar;
